import { ref } from "vue"
import { checkForUpdate } from "@/utils/versionCheck"

declare const __APP_VERSION__: string

// Singleton state for PWA update management
const updateAvailable = ref(false)
const isCheckingUpdate = ref(false)
const currentVersion = __APP_VERSION__
let updateSWCallback: (() => Promise<void>) | null = null

/**
 * Register the service worker update callback
 * This should be called from main.ts when registerSW is initialized
 */
export function registerUpdateCallback(callback: () => Promise<void>) {
  updateSWCallback = callback
}

/**
 * Set update available flag
 * This should be called from main.ts when onNeedRefresh fires
 */
export function setUpdateAvailable(available: boolean) {
  updateAvailable.value = available
}

/**
 * Composable for managing PWA updates
 */
export function usePwaUpdate() {
  /**
   * Check if a new version is available
   */
  async function checkUpdate() {
    if (isCheckingUpdate.value) return

    isCheckingUpdate.value = true
    try {
      const hasUpdate = await checkForUpdate(currentVersion)
      updateAvailable.value = hasUpdate
      return hasUpdate
    } finally {
      isCheckingUpdate.value = false
    }
  }

  /**
   * Force reload the app to get the latest version
   * This will:
   * 1. Skip waiting to activate new service worker
   * 2. Clear all caches
   * 3. Hard reload the page
   */
  async function forceReload() {
    try {
      // If we have a service worker update callback, use it
      if (updateSWCallback) {
        await updateSWCallback()
        location.reload()
        return
      }

      // Fallback: manually clear caches and reload
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration()

        if (registration?.waiting) {
          // Tell the waiting service worker to skip waiting
          registration.waiting.postMessage({ type: "SKIP_WAITING" })
        }

        // Clear all caches
        if ("caches" in window) {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)))
        }
      }

      // Hard reload the page
      location.reload()
    } catch (error) {
      logError("Error during force reload:", error)
      // Still try to reload even if cache clearing fails
      location.reload()
    }
  }

  /**
   * Soft reload - just reload the page without clearing caches
   */
  function softReload() {
    location.reload()
  }

  return {
    updateAvailable,
    isCheckingUpdate,
    currentVersion,
    checkUpdate,
    forceReload,
    softReload,
  }
}
import { error as logError } from "@/utils/debug"
